import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  detectPoseCalibrationFrame,
  detectPoseInBothRois,
  detectPoseInRoi,
} from "../../api/poseAnalysisApi";
import { roisFromSuggestedPlayers } from "../../utils/playerRoiFromJoints";
import { COCO_BONE_PAIRS } from "../../utils/poseTimeline";
import {
  geometryModeFromViewSetup,
  isOnTableLeftHalf,
  orderRoisForTableHalf,
  roiCenterNorm,
  splitDiagonalLinePx,
} from "../../utils/tableHalfCourt";
import { containRect } from "../../utils/videoContainCoords";
import {
  LEFT_COLOR,
  RIGHT_COLOR,
  skeletonBoneWidth,
  skeletonJointRadius,
} from "./PoseSkeletonOverlay";
import {
  normalizePoseDetectionPlayer,
} from "../../utils/videoAnalysisSettings";
import { isViewMirroredX } from "../../utils/videoOrientCoords";
import { capturePausedVideoJpeg } from "../../utils/videoFrameCapture";
import "./poseOverlays.css";

function normRect(a, b) {
  const x1 = Math.min(a.x, b.x);
  const y1 = Math.min(a.y, b.y);
  const x2 = Math.max(a.x, b.x);
  const y2 = Math.max(a.y, b.y);
  return {
    x: Math.min(1, Math.max(0, x1)),
    y: Math.min(1, Math.max(0, y1)),
    w: Math.min(1, Math.max(0, x2 - x1)),
    h: Math.min(1, Math.max(0, y2 - y1)),
  };
}

function rectValid(r) {
  return r && r.w >= 0.04 && r.h >= 0.06;
}

function roiStorageCenterX(roi) {
  return roiCenterNorm(roi).x;
}

/** Screen-left center in 0–1, accounting for CSS horizontal mirror. */
function roiVisualCenterX(roi, mirrored) {
  const cx = roiStorageCenterX(roi);
  return mirrored ? 1 - cx : cx;
}

/**
 * Ensure G/D ordering: table-left half first when corners exist, else screen-left.
 */
function orderRoisForScreen(roiA, roiB, validA, validB, mirrored, tableCorners, viewSetup) {
  if (Array.isArray(tableCorners) && tableCorners.length === 4) {
    return orderRoisForTableHalf(
      roiA,
      roiB,
      validA,
      validB,
      tableCorners,
      mirrored,
      viewSetup
    );
  }
  if (!rectValid(roiA) || !rectValid(roiB)) {
    return { roiLeft: roiA, roiRight: roiB, validLeft: validA, validRight: validB };
  }
  const vA = roiVisualCenterX(roiA, mirrored);
  const vB = roiVisualCenterX(roiB, mirrored);
  if (vA <= vB) {
    return { roiLeft: roiA, roiRight: roiB, validLeft: validA, validRight: validB };
  }
  return { roiLeft: roiB, roiRight: roiA, validLeft: validB, validRight: validA };
}

/**
 * Map pointer → video-normalized coords using the same model as <video>:
 * full element box + object-fit contain, with CSS rotate/flip on the element.
 */
function pointerToContainNorm(event, svgEl, videoWidth, videoHeight) {
  if (!svgEl || typeof svgEl.createSVGPoint !== "function") {
    return { x: 0, y: 0, inside: false };
  }
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0, inside: false };
  const vb = svgEl.viewBox?.baseVal;
  const boxW = Math.max(1, vb?.width || svgEl.clientWidth || 1);
  const boxH = Math.max(1, vb?.height || svgEl.clientHeight || 1);
  const rect = containRect(boxW, boxH, videoWidth, videoHeight);
  const pt = svgEl.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const local = pt.matrixTransform(ctm.inverse());
  const x = (local.x - rect.x) / Math.max(1e-6, rect.w);
  const y = (local.y - rect.y) / Math.max(1e-6, rect.h);
  return {
    x: Math.min(1, Math.max(0, x)),
    y: Math.min(1, Math.max(0, y)),
    inside: x >= -0.02 && x <= 1.02 && y >= -0.02 && y <= 1.02,
  };
}

function toContainPx(normX, normY, rect) {
  return {
    x: rect.x + normX * rect.w,
    y: rect.y + normY * rect.h,
  };
}

/**
 * Canvas skeleton locked to the live <video> letterbox (same model as playback).
 */
function VideoLockedSkeletonCanvas({
  videoRef,
  jointsLeft,
  jointsRight,
  videoWidth,
  videoHeight,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const hasJoints =
      (Array.isArray(jointsLeft) && jointsLeft.length >= 17) ||
      (Array.isArray(jointsRight) && jointsRight.length >= 17);
    if (!hasJoints) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      return undefined;
    }

    const paint = () => {
      const video = videoRef?.current || null;
      const cssW = Math.max(1, canvas.clientWidth || 1);
      const cssH = Math.max(1, canvas.clientHeight || 1);
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const bufW = Math.round(cssW * dpr);
      const bufH = Math.round(cssH * dpr);
      if (canvas.width !== bufW || canvas.height !== bufH) {
        canvas.width = bufW;
        canvas.height = bufH;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      const vw = Math.max(1, video?.videoWidth || videoWidth || 1);
      const vh = Math.max(1, video?.videoHeight || videoHeight || 1);
      const rect = containRect(cssW, cssH, vw, vh);

      const drawPlayer = (joints, color, isPlayerA) => {
        if (!Array.isArray(joints) || joints.length < 17) return;
        const lineWidth = skeletonBoneWidth(rect);
        const r = skeletonJointRadius(rect);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        for (const [a, b] of COCO_BONE_PAIRS) {
          const ja = joints[a];
          const jb = joints[b];
          if (!ja || !jb) continue;
          if ((ja.score ?? 1) < 0.2 || (jb.score ?? 1) < 0.2) continue;
          const x1 = rect.x + ja.x * rect.w;
          const y1 = rect.y + ja.y * rect.h;
          const x2 = rect.x + jb.x * rect.w;
          const y2 = rect.y + jb.y * rect.h;
          ctx.save();
          ctx.strokeStyle = color;
          ctx.globalAlpha = 0.28;
          ctx.lineWidth = lineWidth * 2.4;
          ctx.shadowColor = color;
          ctx.shadowBlur = lineWidth * 4;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 0.95;
          ctx.lineWidth = lineWidth;
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
          ctx.restore();
        }
        for (let i = 0; i < joints.length; i++) {
          const j = joints[i];
          if (!j || (j.score ?? 1) < 0.2) continue;
          ctx.save();
          ctx.fillStyle = isPlayerA ? "rgba(255,255,255,0.9)" : color;
          ctx.globalAlpha = 0.9;
          ctx.shadowColor = color;
          ctx.shadowBlur = r * 1.8;
          ctx.beginPath();
          ctx.arc(rect.x + j.x * rect.w, rect.y + j.y * rect.h, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      };

      drawPlayer(jointsLeft, LEFT_COLOR, true);
      drawPlayer(jointsRight, RIGHT_COLOR, false);
    };

    paint();
    const ro = new ResizeObserver(() => paint());
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [videoRef, jointsLeft, jointsRight, videoWidth, videoHeight]);

  const visible =
    (Array.isArray(jointsLeft) && jointsLeft.length >= 17) ||
    (Array.isArray(jointsRight) && jointsRight.length >= 17);
  if (!visible) return null;

  return (
    <canvas
      ref={canvasRef}
      className="player-calib-overlay__skeleton-canvas"
      aria-hidden
    />
  );
}

/**
 * Draw elastic rectangles around Player A (left) and Player B (right).
 * Coach can draw boxes or use Auto-detect (largest shoulder→hip on each half;
 * poses missing shoulder/hip are skipped — same as batch).
 */
export function PlayerCalibrationOverlay({
  tableCorners = null,
  onConfirm,
  onBack,
  onCancel,
  confirming = false,
  videoWidth = 0,
  videoHeight = 0,
  initialRoiLeft = null,
  initialRoiRight = null,
  contentTransform = null,
  viewSetup = null,
  toolbarContainer = null,
  teId = null,
  timeMs = 0,
  videoRef = null,
  /** Exact clip URL currently on screen — used for offscreen capture proxy. */
  videoUrl = null,
  /** A | B | Both — which court-side player(s) to require / auto-detect. */
  detectionPlayer = "A",
}) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const svgRef = useRef(null);
  const validateAbortRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const playerMode = normalizePoseDetectionPlayer(detectionPlayer);
  const geometryMode = geometryModeFromViewSetup(viewSetup);
  const isReferee = geometryMode === "referee";
  const splitHint = isReferee
    ? "the net line through the table center"
    : "the line from top-left to bottom-right table corners";
  const needLeft = playerMode === "A" || playerMode === "Both";
  const needRight = playerMode === "B" || playerMode === "Both";
  const [activeSide, setActiveSide] = useState(needLeft ? "left" : "right");
  const [roiLeft, setRoiLeft] = useState(needLeft ? initialRoiLeft : null);
  const [roiRight, setRoiRight] = useState(needRight ? initialRoiRight : null);
  const [dragStart, setDragStart] = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [validLeft, setValidLeft] = useState(null);
  const [validRight, setValidRight] = useState(null);
  const [validating, setValidating] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [validateError, setValidateError] = useState("");

  useEffect(() => {
    if (!needLeft && activeSide === "left") setActiveSide("right");
    else if (!needRight && activeSide === "right") setActiveSide("left");
  }, [needLeft, needRight, activeSide]);

  const captureFrameContext = useCallback(async (signal) => {
    const videoEl = videoRef?.current || null;
    const frameJpeg = await capturePausedVideoJpeg(videoEl, videoUrl, signal);
    if (!frameJpeg) {
      throw new Error(
        t(
          "poseRoiCaptureFailed",
          "Could not capture this video frame for detection. Try again."
        )
      );
    }
    const captureMs =
      videoEl && Number.isFinite(videoEl.currentTime)
        ? Math.max(0, Math.round(videoEl.currentTime * 1000))
        : timeMs;
    const videoSize = {
      w: videoEl?.videoWidth || videoWidth,
      h: videoEl?.videoHeight || videoHeight,
    };
    return { videoEl, frameJpeg, captureMs, videoSize };
  }, [t, timeMs, videoHeight, videoRef, videoUrl, videoWidth]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => {
      setStageSize({ w: el.clientWidth, h: el.clientHeight });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    return () => validateAbortRef.current?.abort();
  }, []);

  const vw = Math.max(1, videoWidth || stageSize.w || 1);
  const vh = Math.max(1, videoHeight || stageSize.h || 1);

  const contain = useMemo(
    () =>
      containRect(
        Math.max(1, stageSize.w),
        Math.max(1, stageSize.h),
        vw,
        vh
      ),
    [stageSize, vw, vh]
  );

  const orientStyle = useMemo(() => {
    const base = { position: "absolute", inset: 0 };
    if (!contentTransform) return base;
    return {
      ...base,
      transform: contentTransform,
      transformOrigin: "center center",
    };
  }, [contentTransform]);

  const toNorm = useCallback(
    (e) => pointerToContainNorm(e, svgRef.current, vw, vh),
    [vw, vh]
  );

  const draftRect = useMemo(() => {
    if (!dragStart || !dragCurrent) return null;
    return normRect(dragStart, dragCurrent);
  }, [dragStart, dragCurrent]);

  const payloadFromPlayer = (player, imageWidth, imageHeight) => ({
    joints: player.joints,
    imageWidth,
    imageHeight,
  });

  const validateSingleRoi = useCallback(
    async (roi, side) => {
      if (!teId || !rectValid(roi)) return false;
      validateAbortRef.current?.abort();
      const ac = new AbortController();
      validateAbortRef.current = ac;
      setValidating(true);
      setValidateError("");
      if (side === "left") setValidLeft(null);
      else setValidRight(null);

      try {
        const { frameJpeg, captureMs, videoSize } = await captureFrameContext(
          ac.signal
        );
        const data = await detectPoseInRoi(
          teId,
          captureMs,
          roi,
          ac.signal,
          frameJpeg,
          videoSize,
          videoUrl,
          viewSetup
        );
        if (ac.signal.aborted) return false;
        if (!data?.found || !data?.player) {
          setValidateError(
            data?.message ||
              t(
                "poseRoiNoPlayer",
                "No player detected inside this box. Redraw around the full body."
              )
          );
          return false;
        }
        const payload = payloadFromPlayer(
          data.player,
          data.image_width,
          data.image_height
        );
        if (side === "left") setValidLeft(payload);
        else setValidRight(payload);
        return true;
      } catch (err) {
        if (err?.name === "AbortError") return false;
        setValidateError(
          err?.message ||
            t(
              "poseRoiDetectFailed",
              "Could not validate players in these boxes. Please draw them again."
            )
        );
        return false;
      } finally {
        if (!ac.signal.aborted) setValidating(false);
      }
    },
    [captureFrameContext, t, teId, videoUrl, viewSetup]
  );

  const validateBothRois = useCallback(
    async (halfLeftRect, halfRightRect, rawLeft, rawRight) => {
      if (!teId || !rectValid(halfLeftRect) || !rectValid(halfRightRect)) {
        return false;
      }
      if (Array.isArray(tableCorners) && tableCorners.length === 4 && rawLeft && rawRight) {
        const ca = roiCenterNorm(rawLeft);
        const cb = roiCenterNorm(rawRight);
        const aOnLeft = isOnTableLeftHalf(ca.x, ca.y, tableCorners, viewSetup);
        const bOnLeft = isOnTableLeftHalf(cb.x, cb.y, tableCorners, viewSetup);
        if (aOnLeft === bOnLeft) {
          setValidateError(
            t(
              "poseRoiSameHalf",
              isReferee
                ? "Both boxes are on the same side of the table. Place one box on each half, on opposite sides of the net line through the table center."
                : "Both boxes are on the same side of the table. Place one box on each half, on opposite sides of the line from the top-left corner to the bottom-right corner."
            )
          );
          return false;
        }
      }
      validateAbortRef.current?.abort();
      const ac = new AbortController();
      validateAbortRef.current = ac;
      setValidating(true);
      setValidateError("");
      setValidLeft(null);
      setValidRight(null);

      const mapOrderedToState = (orderedLeft, orderedRight, data) => {
        const leftPayload = data.player_left
          ? payloadFromPlayer(
              data.player_left,
              data.image_width,
              data.image_height
            )
          : null;
        const rightPayload = data.player_right
          ? payloadFromPlayer(
              data.player_right,
              data.image_width,
              data.image_height
            )
          : null;
        if (orderedLeft === rawLeft) setValidLeft(leftPayload);
        else if (orderedLeft === rawRight) setValidRight(leftPayload);
        if (orderedRight === rawLeft) setValidLeft(rightPayload);
        else if (orderedRight === rawRight) setValidRight(rightPayload);
      };

      const clearBoth = () => {
        setRoiLeft(null);
        setRoiRight(null);
        setValidLeft(null);
        setValidRight(null);
        setActiveSide(needLeft ? "left" : "right");
      };

      try {
        const { frameJpeg, captureMs, videoSize } = await captureFrameContext(
          ac.signal
        );
        const data = await detectPoseInBothRois(
          teId,
          captureMs,
          halfLeftRect,
          halfRightRect,
          ac.signal,
          frameJpeg,
          videoSize,
          videoUrl,
          viewSetup,
          tableCorners
        );
        if (ac.signal.aborted) return false;

        const ok = Boolean(data?.ok);
        if (!ok) {
          mapOrderedToState(halfLeftRect, halfRightRect, data);
          const errors = Array.isArray(data?.errors) ? data.errors : [];
          if (
            errors.includes("same_player") ||
            errors.includes("rois_same_half") ||
            errors.includes("left_roi_wrong_half") ||
            errors.includes("right_roi_wrong_half") ||
            (!data?.found_left && !data?.found_right)
          ) {
            clearBoth();
          } else if (data?.found_left && data?.found_right) {
            setValidateError(
              data?.message ||
                t(
                  "poseRoiDetectMismatch",
                  "Players were detected but could not be confirmed. Adjust the boxes slightly and try again."
                )
            );
            return false;
          }
          setValidateError(
            data?.message ||
              t(
                "poseRoiSamePlayer",
                `Both boxes detected the same player or the wrong table half. Draw one box on each side of ${splitHint}.`
              )
          );
          return false;
        }

        mapOrderedToState(halfLeftRect, halfRightRect, data);
        return true;
      } catch (err) {
        if (err?.name === "AbortError") return false;
        clearBoth();
        setValidateError(
          err?.message ||
            t(
              "poseRoiDetectFailed",
              "Could not validate players in these boxes. Please draw them again."
            )
        );
        return false;
      } finally {
        if (!ac.signal.aborted) setValidating(false);
      }
    },
    [
      captureFrameContext,
      isReferee,
      needLeft,
      splitHint,
      t,
      tableCorners,
      teId,
      videoUrl,
      viewSetup,
    ]
  );

  const handleAutoDetect = useCallback(async () => {
    if (!teId || confirming || validating || autoDetecting) return;
    validateAbortRef.current?.abort();
    const ac = new AbortController();
    validateAbortRef.current = ac;
    setAutoDetecting(true);
    setValidateError("");
    setValidLeft(null);
    setValidRight(null);

    try {
      const { frameJpeg, captureMs, videoSize } = await captureFrameContext(
        ac.signal
      );
      const data = await detectPoseCalibrationFrame(
        teId,
        captureMs,
        ac.signal,
        frameJpeg,
        videoSize,
        videoUrl,
        viewSetup,
        tableCorners
      );
      if (ac.signal.aborted) return;

      const suggested = roisFromSuggestedPlayers(data);
      const nextLeft = needLeft ? suggested.left : null;
      const nextRight = needRight ? suggested.right : null;

      if (needLeft && !rectValid(nextLeft)) {
        throw new Error(
          t(
            "poseAutoDetectMissingA",
            "Could not auto-detect Player A (left / G). Pause when the player is fully visible, or draw the box manually."
          )
        );
      }
      if (needRight && !rectValid(nextRight)) {
        throw new Error(
          t(
            "poseAutoDetectMissingB",
            "Could not auto-detect Player B (right / D). Pause when the player is fully visible, or draw the box manually."
          )
        );
      }

      setRoiLeft(nextLeft);
      setRoiRight(nextRight);
      if (nextLeft && data?.suggested_left) {
        setValidLeft(
          payloadFromPlayer(
            data.suggested_left,
            data.image_width,
            data.image_height
          )
        );
      }
      if (nextRight && data?.suggested_right) {
        setValidRight(
          payloadFromPlayer(
            data.suggested_right,
            data.image_width,
            data.image_height
          )
        );
      }
      if (!nextLeft && needRight) setActiveSide("right");
      else if (!nextRight && needLeft) setActiveSide("left");
    } catch (err) {
      if (err?.name === "AbortError") return;
      setValidateError(
        err?.message ||
          t(
            "poseAutoDetectFailed",
            "Auto-detect failed. Try again or draw the player boxes manually."
          )
      );
    } finally {
      if (!ac.signal.aborted) setAutoDetecting(false);
    }
  }, [
    autoDetecting,
    captureFrameContext,
    confirming,
    needLeft,
    needRight,
    t,
    tableCorners,
    teId,
    validating,
    videoUrl,
    viewSetup,
  ]);

  const busy = confirming || validating || autoDetecting;

  const onPointerDown = (e) => {
    if (busy) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toNorm(e);
    if (!pt.inside) return;
    svgRef.current?.setPointerCapture?.(e.pointerId);
    setDragStart({ x: pt.x, y: pt.y });
    setDragCurrent({ x: pt.x, y: pt.y });
  };

  const onPointerMove = (e) => {
    if (!dragStart || busy) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toNorm(e);
    setDragCurrent({ x: pt.x, y: pt.y });
  };

  const onPointerUp = (e) => {
    if (!dragStart) return;
    e.preventDefault();
    e.stopPropagation();
    const pt = toNorm(e);
    const end = dragCurrent || { x: pt.x, y: pt.y };
    const rect = normRect(dragStart, end);
    setDragStart(null);
    setDragCurrent(null);
    if (!rectValid(rect)) return;
    setValidateError("");
    if (activeSide === "left") {
      setRoiLeft(rect);
      setValidLeft(null);
      if (needRight && !roiRight) setActiveSide("right");
    } else {
      setRoiRight(rect);
      setValidRight(null);
      if (needLeft && !roiLeft) setActiveSide("left");
    }
  };

  const clearSide = (side) => {
    validateAbortRef.current?.abort();
    setValidating(false);
    setAutoDetecting(false);
    setValidateError("");
    if (side === "left") {
      setRoiLeft(null);
      setValidLeft(null);
      setActiveSide("left");
    } else {
      setRoiRight(null);
      setValidRight(null);
      setActiveSide("right");
    }
  };

  const mirroredX = useMemo(
    () => isViewMirroredX(contentTransform),
    [contentTransform]
  );

  const ordered = useMemo(
    () =>
      orderRoisForScreen(
        roiLeft,
        roiRight,
        validLeft,
        validRight,
        mirroredX,
        tableCorners,
        viewSetup
      ),
    [roiLeft, roiRight, validLeft, validRight, mirroredX, tableCorners, viewSetup]
  );

  const hasTableSplit =
    Array.isArray(tableCorners) && tableCorners.length === 4;
  const splitLine = useMemo(
    () =>
      hasTableSplit
        ? splitDiagonalLinePx(tableCorners, contain, viewSetup)
        : null,
    [hasTableSplit, tableCorners, contain, viewSetup]
  );

  const requiredBoxesDrawn =
    (!needLeft || rectValid(ordered.roiLeft)) &&
    (!needRight || rectValid(ordered.roiRight));

  const canConfirm = requiredBoxesDrawn && !busy;

  const handleConfirmPlayers = useCallback(async () => {
    if (!canConfirm) return;
    if (playerMode === "Both") {
      const ok = await validateBothRois(
        ordered.roiLeft,
        ordered.roiRight,
        roiLeft,
        roiRight
      );
      if (!ok) return;
      onConfirm?.({
        roiLeft: ordered.roiLeft,
        roiRight: ordered.roiRight,
        swapPlayers: false,
      });
      return;
    }
    if (playerMode === "B") {
      const roi = ordered.roiRight || roiRight;
      const ok = await validateSingleRoi(roi, "right");
      if (!ok) return;
      onConfirm?.({
        roiLeft: null,
        roiRight: roi,
        swapPlayers: false,
      });
      return;
    }
    const roi = ordered.roiLeft || roiLeft;
    const ok = await validateSingleRoi(roi, "left");
    if (!ok) return;
    onConfirm?.({
      roiLeft: roi,
      roiRight: null,
      swapPlayers: false,
    });
  }, [
    canConfirm,
    ordered.roiLeft,
    ordered.roiRight,
    onConfirm,
    playerMode,
    roiLeft,
    roiRight,
    validateBothRois,
    validateSingleRoi,
  ]);

  const hint = autoDetecting
    ? t(
        "poseAutoDetecting",
        "Auto-detecting player(s) on this frame…"
      )
    : validating
      ? playerMode === "Both"
        ? t(
            "poseRoiValidatingBoth",
            "Checking for both players inside the boxes…"
          )
        : t(
            "poseRoiValidatingOne",
            "Checking for a player inside the box…"
          )
      : playerMode === "Both"
        ? !roiLeft
          ? hasTableSplit
            ? t(
                "poseRoiDrawA",
                `Draw a box around Player A (G) on their side of ${splitHint}.`
              )
            : t(
                "poseRoiDrawA",
                "Draw a box around the player on the LEFT of the screen (Player A / G)."
              )
          : !roiRight
            ? hasTableSplit
              ? t(
                  "poseRoiDrawB",
                  `Draw a box around Player B (D) on the other side of ${splitHint}.`
                )
              : t(
                  "poseRoiDrawB",
                  "Draw a box around the player on the RIGHT of the screen (Player B / D)."
                )
            : hasTableSplit
              ? t(
                  "poseRoiConfirm",
                  isReferee
                    ? "Both boxes drawn on opposite sides of the net. Confirm to validate and analyze."
                    : "Both boxes drawn on opposite sides of the TL→BR diagonal. Confirm to validate and analyze."
                )
              : t(
                  "poseRoiConfirm",
                  "Both boxes drawn. A = screen left, B = screen right. Confirm to validate and analyze."
                )
        : playerMode === "B"
          ? !roiRight
            ? t(
                "poseRoiDrawBOnly",
                "Draw a box around Player B (right / D), or use Auto-detect."
              )
            : t(
                "poseRoiConfirmB",
                "Player B box ready. Confirm to validate and analyze with ROI tracking."
              )
          : !roiLeft
            ? t(
                "poseRoiDrawAOnly",
                "Draw a box around Player A (left / G), or use Auto-detect."
              )
            : t(
                "poseRoiConfirmA",
                "Player A box ready. Confirm to validate and analyze with ROI tracking."
              );

  const renderNormRect = (rect, color, label, dashed = false, ok = false) => {
    if (!rect || !contain) return null;
    const x = contain.x + rect.x * contain.w;
    const y = contain.y + rect.y * contain.h;
    const w = rect.w * contain.w;
    const h = rect.h * contain.h;
    const strokeW = Math.max(2, Math.min(contain.w, contain.h) * 0.004);
    return (
      <g>
        <rect
          x={x}
          y={y}
          width={w}
          height={h}
          fill={`${color}22`}
          stroke={ok ? "#4ADE80" : color}
          strokeWidth={strokeW}
          strokeDasharray={dashed ? `${strokeW * 3} ${strokeW * 2}` : undefined}
        />
        <text
          x={x + strokeW}
          y={Math.max(contain.y + 14, y - 6)}
          fill={ok ? "#4ADE80" : color}
          fontSize={Math.max(12, Math.min(contain.w, contain.h) * 0.035)}
          fontFamily="sans-serif"
          fontWeight="700"
        >
          {label}
          {ok ? " ✓" : ""}
        </text>
      </g>
    );
  };

  const vbW = Math.max(1, stageSize.w);
  const vbH = Math.max(1, stageSize.h);

  const toolbar = (
    <div className="table-calib-overlay__toolbar">
      <p className="table-calib-overlay__hint">{hint}</p>
      {validateError ? (
        <p className="table-calib-overlay__error">{validateError}</p>
      ) : null}
      <div
        className="player-calib-overlay__sides"
        role="group"
        aria-label={t("poseAssignSide", "Assign side")}
      >
        {needLeft ? (
          <button
            type="button"
            className={`player-calib-overlay__side-btn ${
              activeSide === "left" ? "is-active is-left" : ""
            }`}
            onClick={() => setActiveSide("left")}
            disabled={busy}
          >
            {t("posePlayerA", "Player A (G — TL→BR side)")}
            {validLeft ? " ✓" : roiLeft ? " ●" : ""}
          </button>
        ) : null}
        {needRight ? (
          <button
            type="button"
            className={`player-calib-overlay__side-btn ${
              activeSide === "right" ? "is-active is-right" : ""
            }`}
            onClick={() => setActiveSide("right")}
            disabled={busy}
          >
            {t("posePlayerB", "Player B (D — other side)")}
            {validRight ? " ✓" : roiRight ? " ●" : ""}
          </button>
        ) : null}
      </div>
      <div className="table-calib-overlay__actions">
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onCancel}
          disabled={busy}
        >
          {t("cancel", "Cancel")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={onBack}
          disabled={busy}
        >
          {t("back", "Back")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={() => clearSide(activeSide)}
          disabled={busy}
        >
          {t("poseClearRoi", "Clear box")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn"
          onClick={handleAutoDetect}
          disabled={busy || !teId}
          title={t(
            "poseAutoDetectHint",
            "Detect player(s) on this frame (largest shoulder→hip on each half; skip if hip/shoulder missing), then track the joints box as ROI."
          )}
        >
          {autoDetecting
            ? t("poseAutoDetectingShort", "Detecting…")
            : t("poseAutoDetect", "Auto-detect")}
        </button>
        <button
          type="button"
          className="table-calib-overlay__btn table-calib-overlay__btn--primary"
          disabled={!canConfirm}
          onClick={handleConfirmPlayers}
        >
          {confirming
            ? t("saving", "Saving…")
            : validating
              ? t("poseRoiValidatingShort", "Validating…")
              : t("poseConfirmPlayers", "Confirm players")}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="table-calib-overlay table-calib-overlay--draw player-calib-overlay">
        <div ref={stageRef} className="table-calib-overlay__stage">
          {/* Same model as <video>: full box + CSS transform, contain drawn inside. */}
          <div className="table-calib-overlay__orient" style={orientStyle}>
            <svg
              ref={svgRef}
              className="table-calib-overlay__svg table-calib-overlay__svg--hit"
              viewBox={`0 0 ${vbW} ${vbH}`}
              preserveAspectRatio="none"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <rect
                x={0}
                y={0}
                width={vbW}
                height={vbH}
                fill="rgba(0,0,0,0.001)"
              />
              {Array.isArray(tableCorners) && tableCorners.length === 4 ? (
                <polygon
                  points={tableCorners
                    .map((c) => {
                      const p = toContainPx(c.x, c.y, contain);
                      return `${p.x},${p.y}`;
                    })
                    .join(" ")}
                  fill="rgba(255,220,80,0.1)"
                  stroke="rgba(255,220,80,0.85)"
                  strokeWidth={Math.max(2, Math.min(contain.w, contain.h) * 0.003)}
                />
              ) : null}
              {splitLine ? (
                <line
                  x1={splitLine.x1}
                  y1={splitLine.y1}
                  x2={splitLine.x2}
                  y2={splitLine.y2}
                  stroke="rgba(255,220,80,0.65)"
                  strokeWidth={Math.max(2, Math.min(contain.w, contain.h) * 0.003)}
                  strokeDasharray="8 6"
                />
              ) : null}
              {renderNormRect(
                ordered.roiLeft,
                LEFT_COLOR,
                "A / G",
                false,
                Boolean(ordered.validLeft)
              )}
              {renderNormRect(
                ordered.roiRight,
                RIGHT_COLOR,
                "B / D",
                false,
                Boolean(ordered.validRight)
              )}
              {renderNormRect(
                draftRect,
                activeSide === "left" ? LEFT_COLOR : RIGHT_COLOR,
                activeSide === "left" ? "A…" : "B…",
                true
              )}
            </svg>
            <VideoLockedSkeletonCanvas
              videoRef={videoRef}
              jointsLeft={ordered.validLeft?.joints}
              jointsRight={ordered.validRight?.joints}
              videoWidth={vw}
              videoHeight={vh}
            />
          </div>
        </div>
        {autoDetecting || validating ? (
          <div className="player-calib-overlay__loading" aria-live="polite">
            {autoDetecting
              ? t(
                  "poseAutoDetecting",
                  "Auto-detecting player(s) on this frame…"
                )
              : playerMode === "Both"
                ? t(
                    "poseRoiValidatingBoth",
                    "Checking for both players inside the boxes…"
                  )
                : t(
                    "poseRoiValidatingOne",
                    "Checking for a player inside the box…"
                  )}
          </div>
        ) : null}
      </div>
      {toolbarContainer
        ? createPortal(toolbar, toolbarContainer)
        : (
          <div className="table-calib-overlay__toolbar-fallback">
            {toolbar}
          </div>
        )}
    </>
  );
}

export default PlayerCalibrationOverlay;
